/**
 * Centralized API Client Configuration
 * 
 * This file provides a single source of truth for all API endpoints.
 * It automatically detects the environment and uses the appropriate API URL.
 * 
 * Environment Variables:
 * - REACT_APP_API_URL: Set this in .env.local or .env.production
 *   Example: http://localhost:1337 (development) or https://api.example.com (production)
 */

// Detect API base URL from environment or use sensible defaults
const getAPIBaseURL = (): string => {
  // First priority: explicit environment variable
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Second priority: production environment
  if (process.env.NODE_ENV === 'production') {
    // In production, if REACT_APP_API_URL is not set, use the current domain
    // This allows serving frontend and backend from the same host
    return window.location.origin;
  }

  // Default: localhost for development
  return 'http://localhost:1337';
};

export const API_BASE_URL = getAPIBaseURL();

/**
 * All API endpoints organized by feature
 */
export const API_ENDPOINTS = {
  // Authentication & User Management
  AUTH: {
    LOGIN: '/api/auth/local',
    REGISTER: '/api/auth/local/register',
    ME: '/api/users/me',
    FORGOT_PASSWORD: '/api/auth/forgot-password',
    RESET_PASSWORD: '/api/auth/reset-password',
    EMAIL_CONFIRMATION: '/api/auth/email-confirmation',
    GOOGLE_CALLBACK: '/api/auth/google/callback',
    GOOGLE_AUTH: '/api/auth/google',
  },

  // Approval Requests
  APPROVAL_REQUESTS: {
    LIST: '/api/aproval-requests',
    GET: (id: string) => `/api/aproval-requests/${id}`,
    CREATE: '/api/aproval-requests',
    UPDATE: (id: string) => `/api/aproval-requests/${id}`,
    DELETE: (id: string) => `/api/aproval-requests/${id}`,
  },

  // Item Requests
  ITEM_REQUESTS: {
    LIST: '/api/itemrequests',
    GET: (id: string) => `/api/itemrequests/${id}`,
    CREATE: '/api/itemrequests',
    UPDATE: (id: string) => `/api/itemrequests/${id}`,
    DELETE: (id: string) => `/api/itemrequests/${id}`,
  },

  // Item Estimates
  ITEM_ESTIMATES: {
    LIST: '/api/itemestimates',
    CREATE: '/api/itemestimates',
  },

  // Cost Categories
  COST_CATEGORIES: {
    LIST: '/api/cost-categories',
  },

  // Commitments
  COMMITMENTS: {
    LIST: '/api/commitments',
    GET: (id: string) => `/api/commitments/${id}`,
    CREATE: '/api/commitments',
    UPDATE: (id: string) => `/api/commitments/${id}`,
    DELETE: (id: string) => `/api/commitments/${id}`,
  },

  // Savasin Details
  SAVASIN_DETAILS: {
    LIST: '/api/savasin-details',
    GET: (id: string) => `/api/savasin-details/${id}`,
    CREATE: '/api/savasin-details',
    UPDATE: (id: string) => `/api/savasin-details/${id}`,
    DELETE: (id: string) => `/api/savasin-details/${id}`,
  },

  // Other Sources of Income
  OTHER_SOURCES: {
    LIST: '/api/other-sources-of-incomes',
    GET: (id: string) => `/api/other-sources-of-incomes/${id}`,
    CREATE: '/api/other-sources-of-incomes',
    UPDATE: (id: string) => `/api/other-sources-of-incomes/${id}`,
    DELETE: (id: string) => `/api/other-sources-of-incomes/${id}`,
  },

  // Expenses
  EXPENSES: {
    LIST: '/api/expenses',
    GET: (id: string) => `/api/expenses/${id}`,
    CREATE: '/api/expenses',
    UPDATE: (id: string) => `/api/expenses/${id}`,
    DELETE: (id: string) => `/api/expenses/${id}`,
  },

  // All Members
  ALL_MEMBERS: {
    LIST: '/api/all-members',
    GET: (id: string) => `/api/all-members/${id}`,
    CREATE: '/api/all-members',
    UPDATE: (id: string) => `/api/all-members/${id}`,
    DELETE: (id: string) => `/api/all-members/${id}`,
  },

  // Users & Roles
  USERS: {
    LIST: '/api/users',
    GET_APPROVERS: '/api/users?filters[role][name][$eq]=Approvers&populate=role',
  },

  // File Upload & Management
  UPLOAD: {
    FILE: '/api/upload',
    GET_FILE: (url: string) => url,
  },
};

/**
 * Build a complete URL for an API endpoint
 * @param endpoint API endpoint path (e.g., '/api/users')
 * @param params Optional query parameters
 * @returns Complete URL
 */
export const buildURL = (
  endpoint: string,
  params?: Record<string, any>
): string => {
  const url = new URL(endpoint, API_BASE_URL);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  return url.toString();
};

/**
 * Get the full URL for a file
 * Handles both relative paths and absolute URLs
 * @param filePath File path (e.g., '/uploads/abc123/file.pdf')
 * @returns Complete file URL
 */
export const getFileURL = (filePath: string): string => {
  if (!filePath) return '';
  
  // If it's already an absolute URL, return as-is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Otherwise, append to API base URL
  return `${API_BASE_URL}${filePath}`;
};

/**
 * Default fetch options for API calls
 * @param token Optional JWT token for authentication
 * @returns Fetch options object
 */
export const getDefaultFetchOptions = (token?: string): RequestInit => ({
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  },
});

const config = {
  API_BASE_URL,
  API_ENDPOINTS,
  buildURL,
  getFileURL,
  getDefaultFetchOptions,
};

export default config;
